import type { RefObject } from "react";
import { formatFitmentLabel } from "../lib/catalog";
import type { Product } from "../types/catalog";
import { Icon } from "./Icon";

type Theme = "light" | "dark";

type SiteHeaderProps = {
  canSearchCatalogue: boolean;
  isSearchOpen: boolean;
  query: string;
  routeTitle: string;
  searchInputRef: RefObject<HTMLInputElement | null>;
  searchSuggestions: Product[];
  theme: Theme;
  onChooseSearchSuggestion: (product: Product) => void;
  onQueryChange: (query: string) => void;
  onToggleSearch: () => void;
  onToggleTheme: () => void;
};

export function SiteHeader({
  canSearchCatalogue,
  isSearchOpen,
  query,
  routeTitle,
  searchInputRef,
  searchSuggestions,
  theme,
  onChooseSearchSuggestion,
  onQueryChange,
  onToggleSearch,
  onToggleTheme,
}: SiteHeaderProps) {
  const nextTheme = theme === "light" ? "dark" : "light";

  return (
    <header className={`site-header${isSearchOpen ? " site-header--search-open" : ""}`}>
      <div className="site-header__bar">
        <div className="site-header__identity">
          <a className="brand" href="/" aria-label="Truefit Automotive home">
            <img
              className="brand__logo"
              src={theme === "dark" ? "/truefit-logo.svg" : "/truefit-logo-dark.svg"}
              alt=""
            />
          </a>
          <span className="site-header__route">{routeTitle}</span>
        </div>
        <div className="site-header__controls">
          {canSearchCatalogue ? (
            <div className={`header-search${isSearchOpen ? " header-search--open" : ""}`}>
              <button
                aria-expanded={isSearchOpen}
                aria-controls="catalog-search-panel"
                aria-label={isSearchOpen ? "Close product search" : "Search products"}
                className="button button--ghost button--icon site-header__icon-button"
                title={isSearchOpen ? "Close product search" : "Search products"}
                type="button"
                onClick={onToggleSearch}
              >
                <Icon name="search" />
              </button>
              {isSearchOpen ? (
                <div className="header-search__panel" id="catalog-search-panel" role="search">
                  <div className="header-search__field">
                    <Icon name="search" />
                    <input
                      aria-label="Product name"
                      ref={searchInputRef}
                      type="search"
                      value={query}
                      onChange={(event) => onQueryChange(event.target.value)}
                      placeholder="Search parts"
                    />
                    {query ? (
                      <div className="header-search__menu">
                        {searchSuggestions.length > 0 ? (
                          searchSuggestions.map((product) => (
                            <button
                              className="header-search__option"
                              key={product.id}
                              type="button"
                              onClick={() => onChooseSearchSuggestion(product)}
                            >
                              <span>
                                <strong>{product.name}</strong>
                                <small>{formatFitmentLabel(product)}</small>
                              </span>
                            </button>
                          ))
                        ) : (
                          <p className="header-search__empty">No matching parts</p>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
          <button
            aria-label={`Switch to ${nextTheme} mode`}
            className="button button--ghost button--icon site-header__icon-button"
            title={`Switch to ${nextTheme} mode`}
            type="button"
            onClick={onToggleTheme}
          >
            <Icon name={nextTheme === "dark" ? "moon" : "sun"} />
          </button>
        </div>
      </div>
    </header>
  );
}
